import { Box, Typography } from '@mui/material';
import {
  GridColDef,
  GridFilterInputValue,
  GridFilterOperator,
  getGridStringOperators,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns-tz';
import { FileText } from 'lucide-react';

import { dataGridUtil } from '../../utils/dataGrid.util';

export interface ShipmentOrderGridRow {
  id: number | string;
  code: string;
  shipper_name: string;
  loading_name: string;
  loading_address: string;
  relay_location_name: string;
  relay_location_address: string;
  unloading_name: string;
  unloading_address: string;
  pickup_date: string | null;
  delivery_date: string | null;
  product_count: number | null;
  quantity: number | null;
  weight_kg: number | null;
  volume_m3: number | null;
  billing_amount_total: number;
  registration_user: string;
  registration_datetime: string | null;
  updated_datetime: string | null;
  status_label?: string;
}

const getColumnWidth = (storageKey: string, fallback: number, prefix: string) => (
  dataGridUtil.getSavedColumnWidth(prefix, storageKey) || fallback
);

const formatDate = (value: string | null) => {
  if (!value) return '--';
  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '--';
    return format(parsed, 'yyyy/MM/dd', { timeZone: 'Asia/Tokyo' });
  } catch (error) {
    console.error('Failed to format date:', error);
    return '--';
  }
};

// 表示用に整形した文字列を返す。空値や parse 失敗時は空文字を返し、表示の '--' は valueFormatter 側で処理する。
const formatDateTimeForCell = (value: string | null) => {
  if (!value) return '';
  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return format(parsed, 'yyyy/MM/dd HH:mm', { timeZone: 'Asia/Tokyo' });
  } catch (error) {
    console.error('Failed to format date time:', error);
    return '';
  }
};

// 表示形式（yyyy/MM/dd HH:mm）の文字列に対する大小比較演算子をフロントに用意する。
// サーバーサイドフィルタなのでクライアント側のフィルタロジックは no-op。
// 実際の比較は ShipmentOrder の registration_datetime / updated_datetime ransacker（DATE_FORMAT 経由）が処理する。
const buildServerSideCompareOperator = (value: string, label: string): GridFilterOperator => ({
  value,
  label,
  getApplyFilterFn: () => () => true,
  InputComponent: GridFilterInputValue,
});

// 日時カラムでは startsWith / endsWith / isEmpty / isNotEmpty / isAnyOf は使い道が薄いため除外する。
const ALLOWED_STRING_OPERATORS = ['contains', 'doesNotContain', 'equals', 'doesNotEqual'];

const datetimeStringFilterOperators: GridFilterOperator[] = [
  ...getGridStringOperators().filter((op) => ALLOWED_STRING_OPERATORS.includes(op.value)),
  buildServerSideCompareOperator('>', '＞ より後'),
  buildServerSideCompareOperator('>=', '≧ 以降'),
  buildServerSideCompareOperator('<', '＜ より前'),
  buildServerSideCompareOperator('<=', '≦ 以前'),
];

const formatNumber = (value: number | null, suffix = '') => {
  if (value === null || value === undefined) return '--';
  return `${value.toLocaleString()}${suffix}`;
};

export const createShipmentOrderColumns = (localStorageItemPrefix: string): GridColDef[] => [
  {
    field: 'code',
    headerName: '伝票No',
    width: getColumnWidth('code', 150, localStorageItemPrefix),
    renderCell: (params) => (
      <Box display="flex" alignItems="center" gap={1} sx={{ height: '100%' }}>
        <FileText size={16} />
        <Typography variant="body2">{params.value}</Typography>
      </Box>
    ),
  },
  {
    field: 'shipper_name',
    headerName: '荷主名',
    width: getColumnWidth('shipper_name', 180, localStorageItemPrefix),
  },
  {
    field: 'loading_name',
    headerName: '積地名称',
    width: getColumnWidth('loading_name', 200, localStorageItemPrefix),
  },
  {
    field: 'loading_address',
    headerName: '積地住所',
    width: getColumnWidth('loading_address', 240, localStorageItemPrefix),
  },
  {
    field: 'unloading_name',
    headerName: '降地名称',
    width: getColumnWidth('unloading_name', 200, localStorageItemPrefix),
  },
  {
    field: 'unloading_address',
    headerName: '降地住所',
    width: getColumnWidth('unloading_address', 240, localStorageItemPrefix),
  },
  {
    field: 'pickup_date',
    headerName: '積日',
    width: getColumnWidth('pickup_date', 140, localStorageItemPrefix),
    valueFormatter: (value) => formatDate(value),
  },
  {
    field: 'delivery_date',
    headerName: '降日',
    width: getColumnWidth('delivery_date', 140, localStorageItemPrefix),
    valueFormatter: (value) => formatDate(value),
  },
  {
    field: 'product_count',
    headerName: '商品数',
    width: getColumnWidth('product_count', 120, localStorageItemPrefix),
    type: 'number',
    valueFormatter: (value) => formatNumber(value),
  },
  {
    field: 'quantity',
    headerName: '数量',
    width: getColumnWidth('quantity', 120, localStorageItemPrefix),
    type: 'number',
    valueFormatter: (value) => formatNumber(value),
  },
  {
    field: 'weight_kg',
    headerName: '重量',
    width: getColumnWidth('weight_kg', 140, localStorageItemPrefix),
    type: 'number',
    valueFormatter: (value) => formatNumber(value, 'kg'),
  },
  {
    field: 'volume_m3',
    headerName: '体積',
    width: getColumnWidth('volume_m3', 140, localStorageItemPrefix),
    type: 'number',
    valueFormatter: (value) => formatNumber(value, '㎥'),
  },
  {
    field: 'billing_amount_total',
    headerName: '請求金額合計',
    width: getColumnWidth('billing_amount_total', 160, localStorageItemPrefix),
    type: 'number',
    valueFormatter: (value: string) => {
      if (!value) return '¥0';
      return `¥${value.toLocaleString()}`;
    },
  },
  {
    field: 'registration_user',
    headerName: '登録担当者',
    width: getColumnWidth('registration_user', 160, localStorageItemPrefix),
  },
  {
    // valueGetter で表示形式の文字列を返し、文字列演算子（含む / 完全一致 / 大小比較）で
    // 「2026/04/24」「2026/04/24 14:28」のいずれでも検索可能にする。バックエンドの ransacker と整合する形で
    // フォーマットしているため、表示通りに入力すればそのまま検索条件として一致判定される。
    // 空値表示の '--' は他カラムと統一するため valueFormatter 側で扱う。
    field: 'registration_datetime',
    headerName: '登録日時',
    width: getColumnWidth('registration_datetime', 180, localStorageItemPrefix),
    valueGetter: (value: string | null) => formatDateTimeForCell(value),
    valueFormatter: (value: string) => value || '--',
    filterOperators: datetimeStringFilterOperators,
  },
  {
    field: 'updated_datetime',
    headerName: '更新日時',
    width: getColumnWidth('updated_datetime', 180, localStorageItemPrefix),
    valueGetter: (value: string | null) => formatDateTimeForCell(value),
    valueFormatter: (value: string) => value || '--',
    filterOperators: datetimeStringFilterOperators,
  },
];
